import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Mentor {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  bio: string;
  specialty: string;
  expertise: string[];
  credits?: string[];
  hourly_rate: number;
  rating: number;
  total_sessions: number;
  is_active: boolean;
  tier: 'free' | 'pro';
  profile_image_url?: string;
  preferred_platform?: string;
  platform_details?: string;
  discovery_call_enabled?: boolean;
}

// NO FALLBACK DATA - ONLY REAL DATABASE INFORMATION

export const useMentors = () => {
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [loading, setLoading] = useState(true); // Show loading until real data loads
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMentors();
  }, []);

  const fetchMentors = async () => {
    console.log('🔍 DIRECT SIMPLE QUERY - This WILL work...');
    setLoading(true);
    setError(null);
    
    try {
      // Use the absolute simplest query possible with minimal fields
      const response = await fetch(`https://llcojpvgertoimsualob.supabase.co/rest/v1/mentors?is_active=eq.true&is_approved=eq.true&select=id,first_name,last_name,bio,specialty,expertise,credits,hourly_rate,rating,total_sessions,is_active,tier,profile_image_url,preferred_platform,platform_details,discovery_call_enabled`, {
        headers: {
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxsY29qcHZnZXJ0b2ltc3VhbG9iIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQyNzMwODAsImV4cCI6MjA2OTg0OTA4MH0.mPKMSynGdAZknGpnaZFqY9Xa1S8cxg2u1LlpJfiRc5I',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxsY29qcHZnZXJ0b2ltc3VhbG9iIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQyNzMwODAsImV4cCI6MjA2OTg0OTA4MH0.mPKMSynGdAZknGpnaZFqY9Xa1S8cxg2u1LlpJfiRc5I',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data || data.length === 0) {
        console.log('⚠️ No mentors found in response');
        setMentors([]);
        setError('No mentors available at this time');
        setLoading(false);
        return;
      }

      console.log('🔍 Direct query returned:', data.length, 'mentors');
      console.log('📋 Mentor names:', data.map((m: any) => `${m.first_name} ${m.last_name}`));

      // Map to our interface
      const mappedMentors: Mentor[] = data.map((mentor: any) => ({
        id: mentor.id,
        first_name: mentor.first_name,
        last_name: mentor.last_name,
        email: '',
        bio: mentor.bio || '',
        specialty: mentor.specialty || '',
        expertise: Array.isArray(mentor.expertise) ? mentor.expertise : [],
        credits: Array.isArray(mentor.credits) ? mentor.credits : [],
        hourly_rate: mentor.hourly_rate || 0,
        rating: mentor.rating || 0,
        total_sessions: mentor.total_sessions || 0,
        is_active: mentor.is_active,
        tier: (mentor.tier as 'free' | 'pro') || 'free',
        profile_image_url: mentor.profile_image_url,
        preferred_platform: mentor.preferred_platform || 'zoom',
        platform_details: mentor.platform_details || '',
        discovery_call_enabled: mentor.discovery_call_enabled || false
      }));

      // Sort mentors
      const sortedMentors = mappedMentors.sort((a, b) => {
        if (a.first_name === 'BLAQOUT') return -1;
        if (b.first_name === 'BLAQOUT') return 1;
        if (a.tier === 'pro' && b.tier !== 'pro') return -1;
        if (b.tier === 'pro' && a.tier !== 'pro') return 1;
        if (a.rating > 0 && b.rating > 0) {
          if (a.rating !== b.rating) return b.rating - a.rating;
          return b.total_sessions - a.total_sessions;
        }
        if (a.rating > 0) return -1;
        if (b.rating > 0) return 1;
        return a.first_name.localeCompare(b.first_name);
      });
      
      console.log('✅ DIRECT QUERY SUCCESS: Mentors loaded:', sortedMentors.length);
      setMentors(sortedMentors);
      setLoading(false);
    } catch (err: any) {
      console.error('❌ Direct query error:', err.message);
      setMentors([]);
      setError('Unable to load mentors');
      setLoading(false);
    }
  };

  return {
    mentors,
    loading,
    error,
    refetch: fetchMentors
  };
};
